import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, AlertTriangle, Clock, Receipt, CheckCircle2, Loader2 } from 'lucide-react';
import { log } from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Net-30 A/R aging chip + inline drill-down.
 *
 * Renders a 4-bucket summary card (current / due_soon / due_now /
 * overdue) on the admin Order Management tab. Clicking the card
 * expands an inline table of every outstanding invoice-mode order
 * sorted newest-first.
 *
 * Hidden entirely when no invoice-mode orders exist (no chip = no
 * AR liability — keeps the dashboard quiet for pay_now-only stores).
 *
 * Data source: GET /api/admin/invoices/aging — single round-trip,
 * no polling. Re-fetches on the parent's `refreshKey` when admin
 * flips a customer's billing mode (so the chip reflects fresh state).
 */
const formatCurrency = (amt, currency = 'CAD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency, maximumFractionDigits: 2,
  }).format(amt || 0);

const BUCKET_META = {
  current: {
    label: '0–13 days', tone: 'emerald',
    Icon: Clock, hint: 'On-time invoices',
  },
  due_soon: {
    label: '14–20 days', tone: 'amber',
    Icon: FileText, hint: 'Approaching Net-30',
  },
  due_now: {
    label: '21–29 days', tone: 'orange',
    Icon: AlertTriangle, hint: 'Send a friendly reminder',
  },
  overdue: {
    label: '30+ days', tone: 'red',
    Icon: AlertTriangle, hint: 'Net-30 elapsed — overdue',
  },
};

const TONE_CLASSES = {
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  amber:   'bg-amber-50   border-amber-200   text-amber-900',
  orange:  'bg-orange-50  border-orange-200  text-orange-900',
  red:     'bg-red-50     border-red-200     text-red-900',
};

const TONE_ACCENTS = {
  emerald: 'text-emerald-700',
  amber:   'text-amber-700',
  orange:  'text-orange-700',
  red:     'text-red-700',
};

/**
 * Quick "Mark paid" inline menu — primary action settles the invoice
 * with no method tag (operator can edit later in OrderManagementModal).
 * The dropdown caret reveals method-tagged variants (Cheque / Wire /
 * EFT / Cash) so the AR record carries the right metadata for accounting.
 *
 * Closes itself on outside-click; stays open while busy. Disabled +
 * shows a spinner while the parent's `busy` flag is true.
 */
const MarkPaidMenu = ({ order, onPick, busy }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    // Defer so the click that opened the menu doesn't immediately close it.
    const t = setTimeout(() => window.addEventListener('click', close), 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('click', close);
    };
  }, [open]);

  if (busy) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200"
        data-testid={`admin-invoice-mark-paid-busy-${order.order_id}`}
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Settling…
      </span>
    );
  }

  return (
    <span className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => onPick(order, null)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-l-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
        data-testid={`admin-invoice-mark-paid-${order.order_id}`}
      >
        <CheckCircle2 className="w-3.5 h-3.5" />
        Mark Paid
      </button>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-2 py-1.5 rounded-r-md bg-emerald-700 hover:bg-emerald-800 text-white text-xs border-l border-emerald-500"
        aria-label="Choose payment method"
        data-testid={`admin-invoice-mark-paid-menu-${order.order_id}`}
      >
        ▾
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-20 bg-white rounded-md shadow-lg border border-gray-200 min-w-[140px] overflow-hidden"
          data-testid={`admin-invoice-mark-paid-options-${order.order_id}`}
        >
          {['cheque', 'wire', 'eft', 'cash', 'other'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => { setOpen(false); onPick(order, m); }}
              className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-emerald-50 hover:text-emerald-800 capitalize"
              data-testid={`admin-invoice-mark-paid-method-${m}-${order.order_id}`}
            >
              Mark paid · {m}
            </button>
          ))}
        </div>
      )}
    </span>
  );
};


export const InvoiceAgingChip = ({ refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(null);  // order_id currently being settled
  const [bumpKey, setBumpKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API_URL}/api/admin/invoices/aging`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((err) => {
        log.warn('[InvoiceAgingChip] fetch failed:', err?.response?.status || err?.name);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshKey, bumpKey]);

  /**
   * Settle an invoice — flips payment_status='paid' on the order,
   * stamps paid_at, and refreshes the chip so the row drops out.
   *
   * Optimistic UX: row instantly enters a `marking` state with a
   * spinner. On success, sonner toast + chip re-fetch (which removes
   * the row). On failure, error toast and chip stays put so the admin
   * can retry.
   */
  const handleMarkPaid = useCallback(async (order, method) => {
    setMarking(order.order_id);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/invoices/${order.order_id}/mark-paid`,
        { paid_method: method || null },
      );
      if (r.data?.already_paid) {
        toast.info(`Invoice ${order.order_number} was already marked paid.`);
      } else {
        toast.success(
          `Invoice ${order.order_number} marked paid${method ? ` (${method})` : ''}.`,
        );
      }
      setBumpKey((k) => k + 1);  // re-fetch
    } catch (err) {
      log.error('[InvoiceAgingChip] mark-paid failed:', err);
      toast.error(
        err?.response?.data?.detail || 'Failed to mark invoice paid.',
      );
    } finally {
      setMarking(null);
    }
  }, []);

  const { totalCount, totalAmount, hasAny } = useMemo(() => {
    const totals = data?.totals || {};
    let count = 0;
    let amount = 0;
    for (const b of Object.keys(totals)) {
      count += totals[b]?.count || 0;
      amount += totals[b]?.amount || 0;
    }
    return { totalCount: count, totalAmount: amount, hasAny: count > 0 };
  }, [data]);

  // Hide the entire chip when there's no AR — keeps the page tidy
  // for stores that only run pay_now customers.
  if (!loading && !hasAny) return null;

  return (
    <div
      className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden"
      data-testid="admin-invoice-aging-chip"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
        data-testid="admin-invoice-aging-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
            <Receipt className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Net-30 outstanding invoices
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {loading
                ? 'Loading…'
                : `${totalCount} open invoice${totalCount === 1 ? '' : 's'} · ${formatCurrency(totalAmount)} due`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!loading && Object.entries(data?.totals || {}).map(([bucket, t]) => {
            if ((t.count || 0) === 0) return null;
            const meta = BUCKET_META[bucket];
            return (
              <span
                key={bucket}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${TONE_CLASSES[meta.tone]}`}
                data-testid={`admin-invoice-aging-pill-${bucket}`}
                title={meta.hint}
              >
                <meta.Icon className="w-3 h-3" />
                {t.count} · {meta.label}
              </span>
            );
          })}
          <span className="text-xs text-gray-400 ml-2">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {expanded && !loading && (data?.orders?.length || 0) > 0 && (
        <div className="border-t border-gray-200" data-testid="admin-invoice-aging-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <th className="text-left py-2.5 px-5 font-semibold">Invoice #</th>
                <th className="text-left py-2.5 px-5 font-semibold">Customer</th>
                <th className="text-left py-2.5 px-5 font-semibold">Sent</th>
                <th className="text-left py-2.5 px-5 font-semibold">Due</th>
                <th className="text-right py-2.5 px-5 font-semibold">Amount</th>
                <th className="text-left py-2.5 px-5 font-semibold">Bucket</th>
                <th className="text-right py-2.5 px-5 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.orders.map((o) => {
                const meta = BUCKET_META[o.bucket];
                return (
                  <tr
                    key={o.order_id}
                    className="border-t border-gray-100 hover:bg-gray-50"
                    data-testid={`admin-invoice-aging-row-${o.order_id}`}
                  >
                    <td className="py-2.5 px-5 font-mono text-xs text-gray-700">
                      {o.order_number}
                    </td>
                    <td className="py-2.5 px-5 text-gray-800">
                      <div className="font-medium">{o.customer_name?.trim() || '—'}</div>
                      <div className="text-xs text-gray-500">{o.customer_email}</div>
                    </td>
                    <td className="py-2.5 px-5 text-gray-600 text-xs whitespace-nowrap">
                      {new Date(o.invoice_email_sent_at).toLocaleDateString()}
                      <span className="text-gray-400 ml-1">({o.age_days}d)</span>
                    </td>
                    <td className="py-2.5 px-5 text-gray-600 text-xs">
                      {o.invoice_due_date || '—'}
                    </td>
                    <td className="py-2.5 px-5 text-right font-semibold tabular-nums">
                      {formatCurrency(o.amount, o.currency)}
                    </td>
                    <td className="py-2.5 px-5">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${TONE_CLASSES[meta.tone]}`}
                      >
                        <meta.Icon className={`w-3 h-3 ${TONE_ACCENTS[meta.tone]}`} />
                        {meta.label}
                      </span>
                    </td>
                    <td className="py-2.5 px-5 text-right">
                      <MarkPaidMenu
                        order={o}
                        onPick={handleMarkPaid}
                        busy={marking === o.order_id}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoiceAgingChip;
